<script setup lang="ts">
import { ref, computed } from 'vue';
import { useProductStore } from '../stores';
import { useNotificationStore } from '../stores/notificationStore';
import { useConfigStore } from '../stores/configStore';
import { useLaboratoryStore } from '../stores/laboratoryStore';
import type { ProductWithDetails } from '../types';
import ProductTable from './price-list/ProductTable.vue';
import ProductFilters from './product/ProductFilters.vue';
import { processProducts } from '../utils/productUtils';
import ProductEditModal from './product/ProductEditModal.vue';

const productStore = useProductStore();
const notificationStore = useNotificationStore();
const configStore = useConfigStore();
const laboratoryStore = useLaboratoryStore();

const searchTerm = ref('');
const showEditModal = ref(false);
const selectedProduct = ref<ProductWithDetails | null>(null);
const selectedCategories = ref<string[]>([]);
const selectedLaboratory = ref('');

// Filtered products
const filteredProducts = computed(() => {
  return productStore.products
    .filter(product => {
      const matchesSearch = searchTerm.value === '' || 
        product.Nombre.toLowerCase().includes(searchTerm.value.toLowerCase());
      
      const matchesCategory = selectedCategories.value.length === 0 || 
        selectedCategories.value.includes(product.Categoria);
        
      const matchesLaboratory = selectedLaboratory.value === '' ||
        product.laboratories.some(lab => lab.laboratory.id === selectedLaboratory.value);

      return matchesSearch && matchesCategory && matchesLaboratory;
    })
    .sort((a, b) => a.Nombre.localeCompare(b.Nombre));
});

// Procesar productos según la configuración de cada categoría
const processedProducts = computed(() => {
  return processProducts(
    filteredProducts.value,
    (categoryId) => configStore.getCategoryConfig(categoryId)
  );
});

// Original products for reference
const originalProducts = computed(() => {
  return productStore.products.filter(product => {
    const matchesCategory = selectedCategories.value.length === 0 || 
      selectedCategories.value.includes(product.Categoria);
    const matchesSearch = !searchTerm.value || 
      product.Nombre.toLowerCase().includes(searchTerm.value.toLowerCase());
    const matchesLaboratory = selectedLaboratory.value === '' ||
      product.laboratories.some(lab => lab.laboratory.id === selectedLaboratory.value);
    return matchesCategory && matchesSearch && matchesLaboratory;
  });
});

// Handle edit product
const handleEdit = (product: ProductWithDetails) => {
  selectedProduct.value = product;
  showEditModal.value = true;
};

// Handle delete product
const handleDelete = async (product: ProductWithDetails) => {
  if (!product.id) return;
  
  if (confirm(`¿Está seguro de que desea eliminar el producto "${product.Nombre}"?`)) {
    try {
      await productStore.deleteProduct(product.id);
      notificationStore.success(`Producto "${product.Nombre}" eliminado correctamente`);
    } catch (error) {
      notificationStore.error('Error al eliminar el producto');
    }
  }
};

// Handle modal close
const handleModalClose = () => {
  showEditModal.value = false;
  selectedProduct.value = null;
};

// Reset all filters
const filtersRef = ref();
const resetFilters = () => {
  searchTerm.value = '';
  selectedLaboratory.value = '';
  if (filtersRef.value) {
    filtersRef.value.resetFilters();
  }
};
</script>

<template>
  <div>
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold text-slate-800">Gestión de Productos</h2>
      <button @click="showEditModal = true; selectedProduct = null" class="btn-primary">
        Nuevo Producto
      </button>
    </div>

    <div class="space-y-0">
      <!-- Filtros de categoría -->
      <ProductFilters
        ref="filtersRef"
        v-model:selected-categories="selectedCategories"
        class="rounded-b-none"
      />

      <!-- Barra de búsqueda y filtros -->
      <div class="flex flex-col md:flex-row items-stretch gap-2 px-4 py-3 bg-white border-x border-b border-slate-200">
        <div class="flex items-center gap-2 flex-grow">
          <font-awesome-icon icon="search" class="text-slate-400 w-4 h-4 flex-shrink-0" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar productos..."
            class="input-field w-full h-9"
          />
        </div>
        
        <!-- Filtro por laboratorio -->
        <div class="w-full md:w-64">
          <select v-model="selectedLaboratory" class="input-field h-9">
            <option value="">Todos los laboratorios</option>
            <option v-for="lab in laboratoryStore.laboratories" :key="lab.id" :value="lab.id">
              {{ lab.Nombre }}
            </option>
          </select>
        </div>
        
        <button 
          v-if="searchTerm || selectedCategories.length || selectedLaboratory"
          @click="resetFilters"
          class="text-sm text-slate-600 hover:text-slate-800 px-3"
        >
          Limpiar filtros
        </button>
      </div>

      <!-- Contador de resultados -->
      <div class="flex justify-between items-center px-4 py-2 bg-white border-x border-b border-slate-200">
        <p class="text-sm text-slate-600">
          {{ processedProducts.length }} productos encontrados
        </p>
      </div>

      <!-- Product Table -->
      <ProductTable 
        :products="processedProducts"
        :original-products="originalProducts"
        :show-actions="true"
        class="rounded-t-none"
        @edit="handleEdit"
        @delete="handleDelete"
      />

      <!-- Mensaje cuando no hay resultados -->
      <div 
        v-if="processedProducts.length === 0" 
        class="text-center py-4 text-slate-600 bg-white border-x border-b border-slate-200 rounded-b-lg"
      >
        No se encontraron productos que coincidan con los filtros seleccionados
      </div>
    </div>

    <!-- Edit Modal -->
    <ProductEditModal
      :show="showEditModal"
      :product="selectedProduct"
      @close="handleModalClose"
      @saved="productStore.fetchProducts()"
    />
  </div>
</template>